import {defineStore} from 'pinia';
import {useAffiliateCustomizationStore} from '~/store/affiliate-customization'
import {useRootStore} from '~/store/root';
import {useUserStore} from '~/store/user';
import env from '~/libs/env'

export const useIdentityStore = defineStore('identity', () => {
  const nuxtApp = useNuxtApp()
  const router = useRouter()
  const { $axios, $lendioCookies } = nuxtApp
  const rootStore = useRootStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const userStore = useUserStore()
  const identityBaseUrl = env('identityServiceUrl')
  const authBaseUrl = `${identityBaseUrl}/auth`
  const updatePasswordApiUrl = `${identityBaseUrl}/identity/update-password`
  const passthroughErrorMessages = [
    'Invalid MFA code',
    'Invalid code',
  ]
  const redirectErrors = [
    'Login attempt is not in the appropriate state',
    'Too many requests',
    'Invalid transaction',
    'Max check attempts reached',
    'Max send attempts reached',
  ]

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/

  const enrollChallengeDestination = ref(null)
  const enrolling = ref(false)
  const forgotPasswordUrl = ref('/identity/forgot-password')
  const jwt = ref('')
  const authHeader = ref(`lendio-jwt ${jwt.value}`)
  const loginUrl = ref('/identity/login')
  const maskedMfaDestination = ref(null)
  const mfaEnrollActivateUrl = ref('/identity/mfa-enroll-activate')
  const mfaEnrollUrl = ref('/identity/mfa-enroll')
  const mfaMethods = ref([])
  const mfaMethodsUrl = ref('/identity/mfa-methods')
  const mfaVerifyUrl = ref('/identity/mfa-verify')
  const passwordExpiredUrl = ref('/identity/password-expired')
  const resetPasswordUrl = ref('/identity/reset-password')
  const businessSelectUrl = ref('/identity/business-select')
  const selectedMfaMethod = ref(null)
  const unrecognized = ref(false)
  const ssnVerificationUrl = ref('/identity/ssn-verification')
  const totpMfaQrCode = ref(null)
  const totpMfaSeed = ref(null)
  const transactionId = ref(null)
  const iframeOnly = ref(null)
  const existingEmail = ref({email: '', exists: false, passwordSet: false})

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/


  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  /**
   * Login the user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<void>}
   */
  async function login(email, password) {
    try {
      const identityLoginResponse = await $axios.post(`${authBaseUrl}/sign-in`, {
        identifier: email,
        password: password,
        tenantId: affiliateCustomizationStore.tenantId,
      }, { withCredentials: true })
      await _handleIdentityTransactionResponse(identityLoginResponse)
    } catch (error) {
      throw error
    }
  }

  /**
   * Start reset password transaction
   * @param {string} identifier - Identifier used to begin reset password
   * @returns {Promise<void>}
   * @throws {Error} - If an error occurs
   */
  async function beginPasswordResetTransaction(identifier) {
    try {
      const response = await $axios.post(`${identityBaseUrl}/auth/reset-password`, {
        identifier,
        tenantId: affiliateCustomizationStore.tenantId,
      })
      await _handleIdentityTransactionResponse(response);
    } catch (error) {
      throw new Error('Password reset request failed. Please try again')
    }
  }

  /**
   * Verify SSN
   * @param {string} ssnLastFour - Last four digits of the user's SSN
   * @returns {Promise<void>}
   * @throws {Error} - If an error occurs
   */
  async function verifySsn(ssnLastFour) {
    try {
      const verifySsnResponse = await $axios.post(`${authBaseUrl}/transaction/ssn-last-four-verify`, {
        ssnLastFour,
        transactionId: transactionId.value,
      })
      await _handleIdentityTransactionResponse(verifySsnResponse)
    } catch (error) {
      throw error
    }
  }

  async function selectMfaMethod(method) {
    selectedMfaMethod.value = method
    if (enrolling.value) {
      if (method === 'totp') {
        await mfaEnroll('totp')
      }
      return router.push(mfaEnrollUrl.value)
    } else {
      await mfaChallenge(method)
      return router.push(mfaVerifyUrl.value)
    }
  }

  /**
   * Enroll in MFA
   * @param {string} method - Method used to enroll in MFA
   * @param {string} to - Destination to send the MFA challenge
   * @param {boolean} resend - If true, resends a previously sent MFA challenge
   * @returns {Promise<void>}
   */
  async function mfaEnroll(method, to = null, resend = false) {
    if (!transactionId.value) {
      return router.push({
        path: loginUrl.value,
        query: {error: 'MFA enrollment failed. Please try again.'}
      })
    }
    try {
      enrollChallengeDestination.value = to
      const enrollResponse = await $axios.post(`${authBaseUrl}/enroll/${method}${resend ? '?resend=true' : ''}`, {
        transactionId: transactionId.value,
        to,
      })
      if (method === 'totp') {
        totpMfaQrCode.value = enrollResponse?.data?.data?.qrCode
        totpMfaSeed.value = enrollResponse?.data?.data?.seed
      } else {
        return router.push(`${mfaEnrollActivateUrl.value}`)
      }
      return
    } catch (error) {
      await _handleMfaError(error, 'MFA enrollment failed. Please try again.')
    }
  }

  /**
   * Activate MFA enrollment
   * @param {string} method - Method used to enroll in MFA
   * @param {string} code - MFA code
   * @param {boolean} rememberDevice - Upon successful MFA enrollment, a cookie will be set to remember this browser and skip MFA verification for a duration determined by identity service
   * @returns {Promise<void>}
   */
  async function mfaEnrollActivate(method, code, rememberDevice) {
    try {
      const enrollResponse = await $axios.post(`${authBaseUrl}/enroll-activate/${method}`, {
        code,
        transactionId: transactionId.value,
        rememberDevice,
      }, { withCredentials: true })
      await _handleIdentityTransactionResponse(enrollResponse)
      enrolling.value = false
    } catch (error) {
      await _handleMfaError(error, 'MFA enrollment failed. Please try again.')
    }
  }

  /**
   * Send an MFA code
   * @param {boolean} resend - If true, resends a previously sent MFA code
   * @returns {Promise<string>}
   */
    async function mfaChallenge(method, resend = false, unrecognized = false) {
      try {
        const challengeResponse = await $axios.post(`${authBaseUrl}/transaction/challenge/${method}${resend ? '?resend=true' : ''}`, {
          transactionId: transactionId.value,
          unrecognized,
        })
        maskedMfaDestination.value = challengeResponse?.data?.data?.maskedDestination?.replace(/\*/g, 'X')
      } catch (error) {
        await _handleMfaError(error, 'Failed to send MFA challenge. Please try again.')
      }
    }

    /**
     * Verify MFA code
     * @param {string} code - MFA code
     * @param {boolean} rememberDevice - Upon successful MFA verification, a cookie will be set to remember this browser and skip MFA verification for a duration determined by identity service
     * @returns {Promise<void>}
     */
    async function mfaVerify(method, code, rememberDevice) {
      try {
        const verifyResponse = await $axios.post(`${authBaseUrl}/transaction/verify/${method}`, {
          code,
          transactionId: transactionId.value,
          rememberDevice,
          unrecognized: unrecognized.value,
        }, { withCredentials: true })
        await _handleIdentityTransactionResponse(verifyResponse)
      } catch (error) {
        await _handleMfaError(error, 'MFA challenge verification failed. Please try again.')
      }
    }

  /**
   * Exchange a one-time use login token for a client token
   * @returns {Promise<void>}
   */
  async function tokenLogin(token) {
    try {
      const exchangeTokenResponse = await $axios.get(`${identityBaseUrl}/auth/token-login`, {
        headers: { Authorization: `lendio-jwt ${token}` }
      })
      await rootStore.setAuthToken(exchangeTokenResponse.data.data)
      await rootStore.getCurrentUser({ forceReload: true })
      await $axios.post(`${env('apiUrl')}/user/last-login`)
    } catch (error) {
      throw error
    }
  }

  /**
   * Reset the expired password
   * @param {string} oldPassword - Old password
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirmed password
   * @returns {Promise<void>}
   */
  async function resetExpiredPassword(oldPassword, newPassword, confirmPassword) {
    try {
      const resetExpiredPasswordResponse = await $axios.post(`${authBaseUrl}/transaction/reset-expired-password`, {
        oldPassword,
        newPassword,
        confirmPassword,
        transactionId: transactionId.value,
      })
      await _handleIdentityTransactionResponse(resetExpiredPasswordResponse)
    } catch (error) {
      if (error?.errors?.[0] === 'Invalid transaction') {
        return router.push({ path: loginUrl.value, query: { error: 'Transaction expired. Please log in to to try again.' } })
      }
      throw error
    }
  }

  /**
   * The final step of the reset password transaction, setting the new password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async function setPassword(newPassword) {
    try {
      const response = await $axios.post(`${identityBaseUrl}/auth/transaction/set-password`, {
        newPassword,
        transactionId: transactionId.value,
      })
      await _handleIdentityTransactionResponse(response)
    } catch (error) {
      throw error
    }
  }

  /**
   * Logout the user
   * @returns {Promise<void>}
   */
  async function logout(path, forceRedirect = true) {
    await $axios.post(`${identityBaseUrl}/auth/logout`).catch((error) => {
      if (error?.response?.status !== 403) {
        log.warning('User Logout Errored', { error })
      }
    })
    await rootStore.destroyUser()
    await userStore.clearAccessToken()
    rootStore.destroyCookiesAndLocalStorage(path)
    // IMPORTANT - hard navigate (external: true) to clear the store
    if (!forceRedirect && window) {
      return window.location.reload()
    }

    // needs /bp included since external is set to true
    return router.push({ path: loginUrl.value }, { external: true, replace: true })
  }

  /**
   *
   * @param oldPassword
   * @param newPassword
   * @returns {Promise<*>}
   */
  async function updatePassword(oldPassword, newPassword) {
    try {
      return await $axios.put(`${updatePasswordApiUrl}`, {
        oldPassword,
        newPassword,
      }, {
        withCredentials: true,
      })
    } catch (error) {
      let responseErrorMessage = error?.response?.data?.errors[0];
      if (responseErrorMessage?.includes(('Unauthorized'))) {
        throw new Error('Old Password is not correct')
      } else if (responseErrorMessage?.includes(('Password must '))) {
        throw new Error(responseErrorMessage)
      }
      throw new Error('We\'re sorry! Something went wrong.')
    }
  }

  async function isIframeOnly() {
    if (iframeOnly.value !== null) {
      return iframeOnly.value
    }
    const headers = useRequestHeaders(['origin'])
    const response = await $axios.get(`${identityBaseUrl}/client/iframe-only`, { headers });
    iframeOnly.value = response.data.data !== false
    return iframeOnly.value
  }

  async function checkForExistingEmail(email, tenantId) {
    if (existingEmail.value.email !== email) {
      const response = await $axios.post(
        `${env('apiUrl')}/borrower-portal/identity/exists`,
        { email, tenantId }
      )
      const data = response.data.data
      existingEmail.value = {
        email,
        exists: data.exists,
        passwordSet: data.passwordSet,
      }
    }
    return existingEmail.value
  }

  /**
   * Private method for consistently handling identity service responses
   * @param {object} response - Response object
   * @returns {Promise<void>}
   * @private
  */
  async function _handleIdentityTransactionResponse(response) {
    if (response?.data?.data?.status) {
      if (response?.data?.data?.transactionId) {
        transactionId.value = response.data.data.transactionId
      }
      switch (response.data.data.status) {
        case 'unrecognized':
          await _handleLoginUnrecognized(response)
          break
        case 'success':
          await _handleLoginSuccess(response)
          break
        case 'ssnVerificationRequired':
          return router.push(ssnVerificationUrl.value)
        case 'mfaEnroll':
          await _handleMfaEnroll(response)
          break
        case 'mfaRequired':
          await _handleMfaRequired(response)
          break
        case 'setPassword':
          await _handleSetPassword(response)
          break
        case 'passwordExpired':
          return router.push(passwordExpiredUrl.value)
          break
        default:
          throw new Error('An error occurred. Please try again.')
      }
    } else {
      throw new Error('An error occurred. Please try again.')
    }
  }

  async function _handleLoginSuccess(response) {
    try {
      selectedMfaMethod.value = null
      unrecognized.value = false
      enrollChallengeDestination.value = null
      transactionId.value = null
      jwt.value = jwt
      await rootStore.setAuthToken(response.data.data.jwt)
      await rootStore.getCurrentUser({ forceReload: true, login: true })
      await $axios.post(`${env('apiUrl')}/user/last-login`)

      return router.push({path: rootStore.getRedirectUrl() }, { replace: true })
    } catch (error) {
      throw error
    }
  }

  async function _handleLoginUnrecognized(response) {
    selectedMfaMethod.value = 'email'
    unrecognized.value = true
    maskedMfaDestination.value = response.data.data.maskedDestination
    await mfaChallenge('email', false, true)
    return router.push(mfaVerifyUrl.value)
  }

  async function _handleMfaEnroll(response) {
    enrolling.value = true
    mfaMethods.value = response.data.data.factorsAllowed

    if (mfaMethods.value.length > 1) {
      return router.push(mfaMethodsUrl.value)
    } else {
      await selectMfaMethod(mfaMethods.value[0])
    }
  }

  async function _handleMfaRequired(response) {
    if (response.data.data.enrolledFactors.length > 1) {
      mfaMethods.value = response.data.data.enrolledFactors
      return router.push(mfaMethodsUrl.value)
    } else {
      selectedMfaMethod.value = response.data.data.enrolledFactors[0]
      await mfaChallenge(response.data.data.enrolledFactors[0])
      return router.push(mfaVerifyUrl.value)
    }
  }

  async function _handleSetPassword(response) {
    return router.push(resetPasswordUrl.value)
  }

  /**
   * Private method for consistently handling MFA errors
   * @param {object} error - Error object
   * @param {string} defaultMessage - Default error message
   * @returns {Promise<void>}
   * @private
   */
  async function _handleMfaError(error, defaultMessage) {
    const errorMessage = error?.response?.data?.errors[0] ?? 'MFA Failed.'
    const shouldRedirect = redirectErrors.some((error) => errorMessage.includes(error))
    if (shouldRedirect) {
      return router.push({
        path: loginUrl.value,
        query: {error: defaultMessage }
      })
    } else {
      const endUserErrorMessage = passthroughErrorMessages.some((error) => errorMessage.includes(error))
        ? errorMessage
        : defaultMessage
      throw new Error(endUserErrorMessage)
    }
  }

  return {
    //state
    authHeader,
    businessSelectUrl,
    enrollChallengeDestination,
    enrolling,
    forgotPasswordUrl,
    iframeOnly,
    existingEmail,
    jwt,
    loginUrl,
    maskedMfaDestination,
    mfaEnrollUrl,
    mfaEnrollActivateUrl,
    mfaMethods,
    mfaMethodsUrl,
    mfaVerifyUrl,
    resetPasswordUrl,
    selectedMfaMethod,
    totpMfaQrCode,
    totpMfaSeed,
    transactionId,
    unrecognized,
    //actions
    beginPasswordResetTransaction,
    isIframeOnly,
    checkForExistingEmail,
    login,
    tokenLogin,
    logout,
    mfaChallenge,
    mfaEnroll,
    mfaEnrollActivate,
    mfaVerify,
    resetExpiredPassword,
    setPassword,
    selectMfaMethod,
    updatePassword,
    verifySsn,
  };
});
